import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AlertConfiguration, AlertKind } from '../../types/dashboard';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, ApiError } from '../../services/apiService';
import CustomButton from '../ui/Button';
import {
  ALERT_LIST_ACCENT_PALETTE,
  countLinkedOutChildren,
  flattenAlertListRows,
  groupAlertConfigurationsForList,
  resolveAlertNumericId,
  type AlertConfigurationListRow,
} from '../../utils/alertConfigurationListRows';

function alertTypeLabel(c: AlertConfiguration): string {
  const k = c.alert_kind as AlertKind | undefined;
  if (k === 'single') return 'Directional Trade';
  if (k === 'directional_multi') return 'Directional Trade';
  if (k === 'bidirectional_multi') return 'Two-Way Trade';
  if (c.is_multi_trigger) return 'Multi (legacy)';
  return 'Single';
}

interface AlertConfigurationsListProps {
  onEdit?: (config: AlertConfiguration) => void;
  onCreateDirectional?: () => void;
  onCreateBidirectional?: () => void;
}

type SortField =
  | 'stock_symbol'
  | 'position_type'
  | 'alert_name'
  | 'alert_type'
  | 'status'
  | 'lastTriggered'
  | 'created';

function getSortValue(config: AlertConfiguration, field: SortField): string | number {
  switch (field) {
    case 'stock_symbol':
      return (config.stock_symbol || config.stockSymbol || '').toUpperCase();
    case 'position_type':
      return (config.position_type || config.positionType || '').toUpperCase();
    case 'alert_name':
      return (config.description || '').toUpperCase();
    case 'alert_type':
      return alertTypeLabel(config);
    case 'status':
      return config.is_active ? 1 : 0;
    case 'lastTriggered':
      return config.last_triggered_at ? new Date(config.last_triggered_at).getTime() : 0;
    case 'created': {
      const createdDate = config.created_at || config.createdAt;
      return createdDate ? new Date(createdDate).getTime() : 0;
    }
    default:
      return '';
  }
}

const AlertConfigurationsList: React.FC<AlertConfigurationsListProps> = ({
  onEdit,
  onCreateDirectional = () => {},
  onCreateBidirectional = () => {},
}) => {
  const { token } = useAuth();
  const [configurations, setConfigurations] = useState<AlertConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; config: AlertConfiguration | null }>({
    open: false,
    config: null,
  });
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const loadConfigurations = useCallback(async () => {
    if (!token) {
      setConfigurations([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getAlertConfigurations(token);
      if (response.success && response.data) {
        setConfigurations(response.data);
      } else {
        console.error('Failed to load alert configurations:', response.error);
        setSnackbar({ open: true, message: 'Failed to load alert configurations', severity: 'error' });
      }
    } catch (error) {
      console.error('Error loading alert configurations:', error);
      setSnackbar({ open: true, message: 'Error loading alert configurations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadConfigurations();
  }, [loadConfigurations]);

  const handleDelete = async (config: AlertConfiguration) => {
    try {
      const response = await apiService.deleteAlertConfiguration(config.id, token || undefined);
      if (response.success) {
        const deletedId = resolveAlertNumericId(config);
        setConfigurations((prev) =>
          prev.filter((c) => {
            const id = resolveAlertNumericId(c);
            const parent = c.linked_directional_alert_id ??
              (c as AlertConfiguration & { linkedDirectionalAlertId?: number }).linkedDirectionalAlertId;
            const parentNum = parent == null ? null : Number(parent);
            if (id === deletedId) return false;
            if (parentNum === deletedId) return false;
            return true;
          }),
        );
        setSnackbar({ open: true, message: 'Alert configuration deleted successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to delete alert configuration', severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting alert configuration:', error);
      const msg =
        error instanceof ApiError
          ? (typeof error.response?.detail === 'string'
              ? error.response.detail
              : error.message)
          : 'Error deleting alert configuration';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setDeleteDialog({ open: false, config: null });
    }
  };

  const getStatusColor = (config: AlertConfiguration): 'success' | 'default' => {
    return config.is_active ? 'success' : 'default';
  };

  const getStatusLabel = (config: AlertConfiguration): string => {
    return config.is_active ? 'Active' : 'Inactive';
  };

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const groupedConfigurations = useMemo(
    () => groupAlertConfigurationsForList(configurations),
    [configurations],
  );

  const sortedGroups = useMemo(() => {
    return [...groupedConfigurations].sort((a, b) => {
      const aValue = getSortValue(a.root, sortField);
      const bValue = getSortValue(b.root, sortField);
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      else if (aValue > bValue) comparison = 1;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [groupedConfigurations, sortField, sortOrder]);

  const displayRows: AlertConfigurationListRow[] = useMemo(
    () => flattenAlertListRows(sortedGroups),
    [sortedGroups],
  );

  const formatLastTriggered = (config: AlertConfiguration) => {
    const raw = config.last_triggered_at;
    if (!raw) {
      return <Typography variant="body2" color="text.secondary">Never</Typography>;
    }
    const d = new Date(raw);
    return (
      <Typography variant="body2">
        {d.toLocaleString()}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Alert Configurations
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" justifyContent="flex-end">
          <Tooltip title="Directional trade with 1 indicator replaces the old single-trigger alert (one slot, one URL).">
            <span>
              <CustomButton variant="primary" onClick={onCreateDirectional} startIcon={<AddIcon />} size="small">
                Directional Trade
              </CustomButton>
            </span>
          </Tooltip>
          <Tooltip title="Create a two-way trade: slot grid, Up/Down/Break webhook groups (Manage Webhooks), optional primary URL, runtime slot state after save.">
            <span>
              <CustomButton
                variant="primary"
                onClick={onCreateBidirectional}
                startIcon={<AddIcon />}
                size="small"
              >
                Two-Way Trade
              </CustomButton>
            </span>
          </Tooltip>
          <CustomButton variant="secondary" onClick={loadConfigurations} startIcon={<RefreshIcon />} size="small">
            Refresh
          </CustomButton>
        </Box>
      </Box>

      {configurations.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Alert Configurations Found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Choose an alert type to get started.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center">
                <CustomButton variant="primary" onClick={onCreateDirectional} startIcon={<AddIcon />}>
                  Directional Trade
                </CustomButton>
                <Tooltip title="Create a two-way trade: slot grid, Up/Down/Break webhook groups (Manage Webhooks), optional primary URL, runtime slot state after save.">
                  <span>
                    <CustomButton variant="primary" onClick={onCreateBidirectional} startIcon={<AddIcon />}>
                      Two-Way Trade
                    </CustomButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'stock_symbol'}
                    direction={sortField === 'stock_symbol' ? sortOrder : 'asc'}
                    onClick={() => handleSort('stock_symbol')}
                  >
                    Stock Symbol
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'position_type'}
                    direction={sortField === 'position_type' ? sortOrder : 'asc'}
                    onClick={() => handleSort('position_type')}
                  >
                    Market Condition
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'alert_name'}
                    direction={sortField === 'alert_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('alert_name')}
                  >
                    Alert Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'alert_type'}
                    direction={sortField === 'alert_type' ? sortOrder : 'asc'}
                    onClick={() => handleSort('alert_type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'lastTriggered'}
                    direction={sortField === 'lastTriggered' ? sortOrder : 'asc'}
                    onClick={() => handleSort('lastTriggered')}
                  >
                    Last Triggered
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'created'}
                    direction={sortField === 'created' ? sortOrder : 'asc'}
                    onClick={() => handleSort('created')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row) => {
                const { config, isGroupedChild, accentColorIndex, showDelete } = row;
                const accent =
                  accentColorIndex >= 0
                    ? ALERT_LIST_ACCENT_PALETTE[accentColorIndex % ALERT_LIST_ACCENT_PALETTE.length]
                    : null;
                const childPl = isGroupedChild ? 4 : undefined;
                const slotIdx =
                  config.linked_directional_slot_index ??
                  (config as AlertConfiguration & { linkedDirectionalSlotIndex?: number }).linkedDirectionalSlotIndex;
                const slotLabel =
                  isGroupedChild && slotIdx != null && !Number.isNaN(Number(slotIdx))
                    ? `Sub-alert · slot ${Number(slotIdx) + 1}`
                    : null;
                return (
                  <TableRow
                    key={config.id}
                    sx={{
                      ...(accent && {
                        boxShadow: () => `inset 12px 0 0 ${accent}`,
                      }),
                      ...(isGroupedChild && {
                        bgcolor: 'action.hover',
                      }),
                    }}
                  >
                    <TableCell sx={{ pl: childPl }}>
                      {slotLabel && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {slotLabel}
                        </Typography>
                      )}
                      <Typography
                        variant="subtitle2"
                        fontWeight={isGroupedChild ? 'medium' : 'bold'}
                        color={isGroupedChild ? 'text.secondary' : 'text.primary'}
                      >
                        {isGroupedChild && (
                          <Typography component="span" variant="inherit" sx={{ mr: 0.5 }} aria-hidden>
                            ↳{' '}
                          </Typography>
                        )}
                        {config.stock_symbol || config.stockSymbol}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pl: childPl }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                        <Chip
                          label={config.position_type || config.positionType}
                          size="small"
                          color={isGroupedChild ? 'default' : 'primary'}
                          variant="outlined"
                        />
                        {(config.position_type || config.positionType || '').toLowerCase() === 'out' &&
                          config.linked_directional_alert_id != null &&
                          config.linked_directional_slot_index != null && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Linked · DMTA #${config.linked_directional_alert_id} · slot ${Number(config.linked_directional_slot_index) + 1}`}
                            />
                          )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ pl: childPl }}>
                      <Typography variant="body2" color={config.description ? 'text.primary' : 'text.secondary'}>
                        {config.description?.trim() || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pl: childPl }}>
                      <Chip label={alertTypeLabel(config)} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ pl: childPl }}>
                      <Chip
                        label={getStatusLabel(config)}
                        size="small"
                        color={getStatusColor(config)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ pl: childPl }}>{formatLastTriggered(config)}</TableCell>
                    <TableCell sx={{ pl: childPl }}>
                      <Typography variant="body2">
                        {config.created_at || config.createdAt
                          ? new Date(config.created_at || config.createdAt!).toLocaleDateString()
                          : 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pl: childPl }}>
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEdit?.(config)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {showDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, config })}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, config: null })}>
        <DialogTitle>Delete Alert Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the alert configuration for{' '}
            {deleteDialog.config?.stock_symbol || deleteDialog.config?.stockSymbol}{' '}
            {deleteDialog.config?.position_type || deleteDialog.config?.positionType}? This action cannot be undone.
          </Typography>
          {deleteDialog.config &&
            (() => {
              const n = countLinkedOutChildren(
                configurations,
                resolveAlertNumericId(deleteDialog.config),
              );
              if (n <= 0) return null;
              return (
                <Typography sx={{ mt: 2 }} color="text.secondary">
                  Legacy linked Out row(s) on this alert will be removed as well ({n}{' '}
                  row{n === 1 ? '' : 's'}).
                </Typography>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, config: null })}>Cancel</Button>
          <Button
            onClick={() => deleteDialog.config && handleDelete(deleteDialog.config)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertConfigurationsList;
